<script setup lang="ts">
import { computed } from 'vue';

import { useUserSupervision } from '@modules/user/composables';

import type {
  VFormFilterButtonEmits,
  VFormFilterButtonProps,
  VFormFilterButtonType,
} from './types';

const props = withDefaults(defineProps<VFormFilterButtonProps>(), {
  type: 'primary',
  addIcon: 'pi pi-plus-circle',
  removeIcon: 'pi pi-trash',
});
const emit = defineEmits<VFormFilterButtonEmits>();

const { isSupervision } = useUserSupervision();

const typeClasses: Record<VFormFilterButtonType, string> = {
  'transparent': 'text-primary border-transparent',
  'transparent-secondary': 'text-muted-color border-gray-200',
  'primary': 'bg-primary text-content-color border-primary',
  'secondary': 'bg-highlight-emphasis border-secondary',
};

const clickableTypeClasses: Record<VFormFilterButtonType, string> = {
  'transparent': 'hover:bg-gray-100 hover:border-gray-100',
  'transparent-secondary': 'hover:bg-gray-100',
  'primary': 'hover:bg-primary/90',
  'secondary': 'hover:bg-highlight/90',
};

const typeClass = computed<string>(() => {
  let className = typeClasses[props.type];

  if (props.isAdd) {
    className += ' font-medium';

    if (isSupervision.value) {
      className += ' p-disabled';
    }
  }

  if (props.isClickable) {
    className += ` cursor-pointer ${clickableTypeClasses[props.type]}`;
  }

  if (props.noBorder) {
    className += ' border-0';
  }

  if (props.noPointerEvents) {
    className += ' pointer-events-none';
  }

  return className;
});
</script>

<template>
  <div v-data-qa-auto-id="'VFormFilterButton'"
    class="flex min-w-16 items-center justify-center gap-2 border px-3 py-2 text-sm rounded-border"
    :class="typeClass"
    @click="emit('click')"
  >
    <i
      v-if="isAdd"
      :class="addIcon"
    />
    <span>{{ label }}</span>
    <i
      v-if="allowExpand"
      class="pi cursor-pointer"
      :class="{
        'pi-angle-up': expanded,
        'pi-angle-down': !expanded,
      }"
      @click="emit('click-expand')"
    />
    <i
      v-if="allowRemove"
      class="cursor-pointer text-sm"
      :class="`${removeIcon}${isSupervision ? ' p-disabled' : ''}`"
      @click.prevent.stop="emit('click-remove')"
    />
  </div>
</template>
